import api from './api';
import { normalizeLanguage } from '../constants/Languages';
import requestDeduplicator from '../utils/requestDeduplicator';
import { readVatClassification, toVatPayload } from '../utils/vatClassification';

const DEFAULT_CURRENCY = 'EUR';

const MEMBERSHIP_BILLING_INTERVAL = {
  Monthly: 'Monthly',
};

const MEMBERSHIP_ENTITLEMENT_TYPE = {
  UnlimitedUse: 'UnlimitedUse',
  FixedAllowancePerPeriod: 'FixedAllowancePerPeriod',
};

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const buildLocalizedPayload = (value) => (
  Object.fromEntries(
    Object.entries(normalizeLocalizedDictionary(value)).map(([language, entryValue]) => [language.toUpperCase(), entryValue.trim()])
  )
);

const normalizeBillingInterval = (value) => (
  value === MEMBERSHIP_BILLING_INTERVAL.Monthly ? MEMBERSHIP_BILLING_INTERVAL.Monthly : MEMBERSHIP_BILLING_INTERVAL.Monthly
);

const normalizeEntitlementType = (value) => (
  value === MEMBERSHIP_ENTITLEMENT_TYPE.FixedAllowancePerPeriod
    ? MEMBERSHIP_ENTITLEMENT_TYPE.FixedAllowancePerPeriod
    : MEMBERSHIP_ENTITLEMENT_TYPE.UnlimitedUse
);

const normalizeServiceIds = (serviceIds) => (
  Array.isArray(serviceIds)
    ? [...new Set(serviceIds.map((serviceId) => Number.parseInt(serviceId, 10)).filter((serviceId) => Number.isInteger(serviceId) && serviceId > 0))]
    : []
);

const normalizeTeamMemberIds = (teamMemberIds) => (
  Array.isArray(teamMemberIds)
    ? [...new Set(teamMemberIds.map((id) => String(id)).filter(Boolean))]
    : []
);

const buildPlanPayload = (plan) => {
  const entitlementType = normalizeEntitlementType(plan.entitlementType);
  const commitmentMonths = plan.commitmentMonths === '' || plan.commitmentMonths == null
    ? null
    : Number.parseInt(plan.commitmentMonths, 10);

  return {
    name: buildLocalizedPayload(plan.name),
    price: Number(plan.price) || 0,
    billingInterval: normalizeBillingInterval(plan.billingInterval),
    entitlementType,
    allowanceQuantity: entitlementType === MEMBERSHIP_ENTITLEMENT_TYPE.FixedAllowancePerPeriod
      ? Number.parseInt(plan.allowanceQuantity, 10) || null
      : null,
    allowCustomerCancellation: Boolean(plan.allowCustomerCancellation),
    commitmentMonths: Number.isInteger(commitmentMonths) && commitmentMonths > 0 ? commitmentMonths : null,
    serviceIds: normalizeServiceIds(plan.serviceIds),
    teamMemberIds: normalizeTeamMemberIds(plan.teamMemberIds),
    ...toVatPayload(plan),
  };
};

const toFormPlan = (plan) => ({
  id: plan?.id ?? null,
  name: normalizeLocalizedDictionary(plan?.name),
  price: Number(plan?.price) || 0,
  currency: plan?.currency || DEFAULT_CURRENCY,
  billingInterval: normalizeBillingInterval(plan?.billingInterval),
  entitlementType: normalizeEntitlementType(plan?.entitlementType),
  allowanceQuantity: plan?.allowanceQuantity ?? '',
  status: plan?.status || 'Active',
  allowCustomerCancellation: Boolean(plan?.allowCustomerCancellation),
  ...readVatClassification(plan),
  commitmentMonths: plan?.commitmentMonths ?? '',
  serviceIds: normalizeServiceIds(plan?.serviceIds),
  teamMemberIds: normalizeTeamMemberIds(plan?.teamMemberIds),
  activeSubscriberCount: Number(plan?.activeSubscriberCount) || 0,
  createdAt: plan?.createdAt ?? null,
});

const isMembershipPlanActive = (status) => status === 'Active';

const MembershipPlanService = {
  toFormPlan,
  isMembershipPlanActive,

  getMembershipPlanManagementData: async (storeId) => (
    requestDeduplicator.deduplicate(`getMembershipPlanManagement-${storeId}`, async () => {
      const [storeResponse, plansResponse] = await Promise.all([
        api.get(`/stores/${storeId}`),
        api.get('/membership-plans', { _storeId: storeId }),
      ]);

      const storeData = storeResponse.data;
      const supportedLanguages = (storeData?.supportedLanguages || ['en'])
        .map((language) => normalizeLanguage(language))
        .filter((language, index, array) => array.indexOf(language) === index);

      const activeServices = Array.isArray(storeData?.services)
        ? storeData.services
          .filter((service) => !service.disabled)
          .map((service) => ({
            id: service.id,
            name: normalizeLocalizedDictionary(service.name),
            consentFormId: service.consentFormId ?? null,
            priceMode: service.priceMode,
            durationMode: service.durationMode,
          }))
        : [];

      return {
        supportedLanguages: supportedLanguages.length > 0 ? supportedLanguages : ['en'],
        currency: storeData?.currency || DEFAULT_CURRENCY,
        appointmentCustomerPaymentMode: storeData?.appointmentCustomerPaymentMode,
        membershipCustomerPaymentMode: storeData?.membershipCustomerPaymentMode,
        services: activeServices,
        plans: Array.isArray(plansResponse.data)
          ? plansResponse.data.map((plan) => toFormPlan(plan))
          : [],
      };
    })
  ),

  createPlan: async (storeId, plan) => {
    const response = await api.post('/membership-plans', buildPlanPayload(plan), { _storeId: storeId });
    return toFormPlan(response.data);
  },

  updatePlan: async (storeId, planId, plan) => {
    const response = await api.put(`/membership-plans/${planId}`, buildPlanPayload(plan), { _storeId: storeId });
    return toFormPlan(response.data);
  },

  deactivatePlan: async (storeId, planId) => {
    await api.post(`/membership-plans/${planId}/deactivate`, null, { _storeId: storeId });
  },

  activatePlan: async (storeId, planId) => {
    await api.post(`/membership-plans/${planId}/activate`, null, { _storeId: storeId });
  },

  getActivePlans: async (storeId) => {
    const response = await api.get('/membership-plans', { _storeId: storeId });
    return (Array.isArray(response.data) ? response.data : [])
      .map((plan) => toFormPlan(plan))
      .filter((plan) => isMembershipPlanActive(plan.status));
  },
};

export default MembershipPlanService;
